"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { SignedOut } from "@clerk/nextjs";
import { Menu, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { ContactSalesModal } from "~/components/marketing/contact-sales-modal";
import { MARKETING_HOME } from "~/lib/inbound-gpt/paths";
import { PublicAuthNavButton } from "~/components/marketing/public-auth-nav-link";
import {
  marketingAppDashboardUrl,
  marketingSiteUrl,
} from "~/lib/auth-paths";
import { MARKETING_BUILD_CUSTOM_NEW } from "~/lib/inbound-gpt/paths";
import { getSiteGlueSignupHref } from "~/lib/siteglue/cta-config";
import { RunFreeAuditNavCta } from "~/components/siteglue/home/run-free-audit-nav-cta";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import { SITEGLUE_PRIMARY_AUDIT_NAV } from "~/lib/siteglue/landing-redesign/copy";
import {
  AgentTemplatesMegaMenu,
  IntegrationsMegaMenu,
  MobileIntegrationsSection,
  MobileAgentTemplatesSection,
  MobileProductSection,
  ProductMegaMenu,
  useMegaMenuState,
} from "~/app/components/marketing-mega-menu";

type MarketingNavProps = {
  /** Force brand during SSR (client getMarketingChrome defaults to EasyGPT without window). */
  brandId?: "siteglue" | "inbound_gpt";
  /** Hire/onboarding builder: hide Contact Sales / Sign In / Get Started Free (CTAs live in builder chrome). */
  hideAuthCtas?: boolean;
  /** Keep Contact Sales + Sign In, but hide Get Started Free (e.g. `/onboard`). */
  hideGetStartedCta?: boolean;
  /** Hide logo lockup (e.g. `/onboard` shows logo in the workspace top bar). */
  hideLogo?: boolean;
};

export default function MarketingNav({
  brandId,
  hideAuthCtas = false,
  hideGetStartedCta = false,
  hideLogo = false,
}: MarketingNavProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contactSalesOpen, setContactSalesOpen] = useState(false);
  const { navRef, openMenu, setOpenMenu, closeMenus } = useMegaMenuState();
  const chrome = getMarketingChrome(
    brandId === "siteglue"
      ? "www.siteglue.ai"
      : brandId === "inbound_gpt"
        ? "www.easygpt.builders"
        : undefined,
  );
  const isSiteGlue = chrome.brandId === "siteglue";

  // When nav is embedded on app.easygpt.builders (hire/onboarding), keep SiteGlue
  // menu links on www.siteglue.ai instead of the app host.
  const siteHref = (path: string) =>
    isSiteGlue ? marketingSiteUrl(path, { brand: "siteglue" }) : path;

  const marketingHomeHref = siteHref(MARKETING_HOME);
  const signUpHref = isSiteGlue
    ? getSiteGlueSignupHref()
    : marketingAppDashboardUrl(MARKETING_BUILD_CUSTOM_NEW);

  const toggleProduct = () => {
    setOpenMenu((current) => (current === "product" ? null : "product"));
  };

  const toggleAgentTemplates = () => {
    setOpenMenu((current) => (current === "agent-templates" ? null : "agent-templates"));
  };

  const toggleIntegrations = () => {
    setOpenMenu((current) => (current === "integrations" ? null : "integrations"));
  };

  useEffect(() => {
    if (!mobileOpen) return;
    closeMenus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen, closeMenus]);

  const closeMobileMenu = () => setMobileOpen(false);

  const mobileFlatLinkClass =
    "flex min-h-12 items-center border-b border-border/70 px-1 text-sm font-semibold text-foreground transition-colors hover:bg-muted/70 active:bg-muted";

  return (
    <>
      <nav
        data-testid="navbar"
        className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      >
        <div
          ref={navRef}
          className="container mx-auto flex h-16 items-center justify-between gap-4 px-4"
        >
          {!hideLogo ? (
            <Link href={marketingHomeHref} className="flex shrink-0 items-center gap-2" data-testid="link-home">
              <Image
                src={chrome.logoSrc}
                alt={chrome.logoAlt}
                width={32}
                height={32}
                className="h-8 w-8 rounded-md"
              />
              <div className="flex flex-col">
                {chrome.brandId === "siteglue" ? (
                  <span className="text-lg font-bold leading-none tracking-tight">
                    <span style={{ color: chrome.primaryColor }}>Site</span>
                    <span style={{ color: chrome.accentColor }}>Glue</span>
                  </span>
                ) : (
                  <span className="text-lg font-bold leading-none tracking-tight">
                    {chrome.productName}
                  </span>
                )}
                <span className="mt-0.5 text-[10px] leading-none text-muted-foreground">
                  {chrome.tagline}
                </span>
              </div>
            </Link>
          ) : (
            <div className="hidden w-0 shrink-0 md:block" aria-hidden />
          )}

          <div className="hidden items-center gap-1 md:flex">
            {isSiteGlue ? (
              <>
                {SITEGLUE_PRIMARY_AUDIT_NAV.map((item) => (
                  <Link
                    key={item.href}
                    href={siteHref(item.href)}
                    className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    data-testid={`link-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={closeMenus}
                  >
                    {item.label}
                  </Link>
                ))}
                <IntegrationsMegaMenu
                  open={openMenu === "integrations"}
                  onToggle={toggleIntegrations}
                  onClose={closeMenus}
                  hrefFor={siteHref}
                />
              </>
            ) : (
              <>
                <ProductMegaMenu
                  brandId={brandId}
                  open={openMenu === "product"}
                  onToggle={toggleProduct}
                  onClose={closeMenus}
                />
                <AgentTemplatesMegaMenu
                  brandId={brandId}
                  open={openMenu === "agent-templates"}
                  onToggle={toggleAgentTemplates}
                  onClose={closeMenus}
                />
                <Link
                  href={siteHref("/how-it-works")}
                  className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  data-testid="link-nav-how-it-works"
                  onClick={closeMenus}
                >
                  How It Works
                </Link>
                <Link
                  href={siteHref("/pricing")}
                  className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  data-testid="link-nav-pricing"
                  onClick={closeMenus}
                >
                  Pricing
                </Link>
                <Link
                  href={siteHref("/about")}
                  className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  data-testid="link-nav-about"
                  onClick={closeMenus}
                >
                  About
                </Link>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {!hideAuthCtas ? (
              <>
                {isSiteGlue ? null : (
                  <button
                    type="button"
                    onClick={() => {
                      setContactSalesOpen(true);
                    }}
                    className="hidden min-h-11 items-center rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:inline-flex"
                    data-testid="link-contact-sales"
                  >
                    Contact sales
                  </button>
                )}
                <PublicAuthNavButton
                  brandId={brandId}
                  className="hidden sm:inline-flex"
                />

                {isSiteGlue ? (
                  <RunFreeAuditNavCta siteHref={siteHref} variant="desktop" />
                ) : !hideGetStartedCta ? (
                  <SignedOut>
                    <Button
                      asChild
                      size="sm"
                      className="hidden text-sm shadow-sm transition-all duration-300 hover:shadow-md sm:inline-flex"
                      data-testid="link-sign-up"
                    >
                      <a href={signUpHref} target="_blank" rel="noopener noreferrer">
                        Sign up
                      </a>
                    </Button>
                  </SignedOut>
                ) : null}
              </>
            ) : null}

            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-muted md:hidden"
              onClick={() => setMobileOpen((o) => !o)}
              data-testid="button-mobile-menu"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileOpen ? (
          <div className="border-t border-border bg-background md:hidden">
            <div className="max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain">
              <div className="flex flex-col px-3 py-2">
                {isSiteGlue ? (
                  <>
                    {SITEGLUE_PRIMARY_AUDIT_NAV.map((item) => (
                      <Link
                        key={item.href}
                        href={siteHref(item.href)}
                        className={mobileFlatLinkClass}
                        data-testid={`mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={closeMobileMenu}
                      >
                        {item.label}
                      </Link>
                    ))}
                    <MobileIntegrationsSection onNavigate={closeMobileMenu} hrefFor={siteHref} />
                  </>
                ) : (
                  <>
                    <MobileProductSection brandId={brandId} onNavigate={closeMobileMenu} />
                    <MobileAgentTemplatesSection
                      brandId={brandId}
                      onNavigate={closeMobileMenu}
                    />
                    <Link
                      href={siteHref("/how-it-works")}
                      className={mobileFlatLinkClass}
                      data-testid="mobile-link-how-it-works"
                      onClick={closeMobileMenu}
                    >
                      How It Works
                    </Link>
                    <Link
                      href={siteHref("/pricing")}
                      className={mobileFlatLinkClass}
                      data-testid="mobile-link-pricing"
                      onClick={closeMobileMenu}
                    >
                      Pricing
                    </Link>
                    <Link
                      href={siteHref("/about")}
                      className={mobileFlatLinkClass}
                      data-testid="mobile-link-about"
                      onClick={closeMobileMenu}
                    >
                      About
                    </Link>
                  </>
                )}

                <div className="sticky bottom-0 mt-2 space-y-2 border-t border-border bg-background/95 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                  {isSiteGlue && !hideAuthCtas ? (
                    <>
                      <RunFreeAuditNavCta
                        siteHref={siteHref}
                        variant="mobile"
                        onNavigate={closeMobileMenu}
                      />
                      <PublicAuthNavButton
                        brandId={brandId}
                        variant="ghost"
                        fullWidth
                        onNavigate={closeMobileMenu}
                      />
                    </>
                  ) : isSiteGlue && hideAuthCtas ? null : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          closeMobileMenu();
                          setContactSalesOpen(true);
                        }}
                        className="flex min-h-11 w-full items-center rounded-md px-3 text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
                        data-testid="mobile-link-contact-sales"
                      >
                        Contact sales
                      </button>
                      <PublicAuthNavButton
                        variant="outline"
                        fullWidth
                        onNavigate={closeMobileMenu}
                      />
                      <SignedOut>
                        <Button
                          asChild
                          size="sm"
                          fullWidth
                          className="text-sm font-medium"
                          data-testid="link-mobile-sign-up"
                        >
                          <a
                            href={signUpHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={closeMobileMenu}
                          >
                            Sign up
                          </a>
                        </Button>
                      </SignedOut>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </nav>

      <ContactSalesModal open={contactSalesOpen} onOpenChange={setContactSalesOpen} />
    </>
  );
}
